/* eslint-disable @typescript-eslint/camelcase */
import {
  AccountEntities,
  AccountTypeDetail,
  DashboardDeliveryPos,
  Hub,
  FeatureFlagDetail,
  ValidationAccountData,
  AccountStatus,
  DeviceAuth
} from "@/domain/entities/Account";
import { AxiosResponse } from "axios";
import {
  ResponsePayload,
  ResponsePayloadV2
} from "@/domain/entities/ResponsePayload";
import { IS_SENDER_ACCOUNT } from "@/app/infrastructures/misc/Utils";
import { AccountController } from "@/app/ui/controllers/AccountController";
import { Hub as HubDetail } from "@/domain/entities/Hub";

export class AccountDataMapper {
  mapperForSenderAcount(data: any) {
    const accountTypeDetail = new AccountTypeDetail({
      countryCode: "ID"
    });

    return new AccountEntities({
      account_id: data.customer_id,
      name: data.fullname,
      role_type: data.role,
      account_type: data.role,
      email: data.email || "-",
      phone_number: data.phone_number.replace("+", ""),
      status: data.customer_status,
      birthdate: data.birthdate,
      customerLevel: data.customer_level,
      account_type_detail: accountTypeDetail,
      watermark: data.watermark
    });
  }

  mapperForNonSenderAccount(data: any) {
    const featureFlag = [...data.nosql_feature_flag, ...data.feature_flag];
    return new AccountEntities({
      account_id: data.account_id,
      username: data.username,
      watermark: data.watermark,
      name: data.name,
      role_type: data.role_type,
      role_permission: data.role_permission,
      account_type: data.account_type.toLowerCase(),
      account_type_detail: data.account_type_detail
        ? new AccountTypeDetail({
            id: data.account_type_detail.id,
            partnerReferralCode: data.account_type_detail.partner_referral_code,
            company_name:
              data.account_type_detail.company_name ||
              data.account_type_detail.name,
            phone: data.account_type_detail.phone,
            address: data.account_type_detail.address,
            balance: data.account_type_detail.balance,
            created_at: data.account_type_detail.created_at,
            updated_at: data.account_type_detail.updated_at,
            type: data.account_type_detail.type,
            isDO: data.account_type_detail.is_do,
            isMixpack: data.account_type_detail.is_mixpack,
            districtCode:
              data.account_type_detail.partner_location?.district_code,
            cityCode: data.account_type_detail.partner_location?.city_code,
            countryCode:
              data.country_code ||
              data.account_type_detail.partner_location?.country.code ||
              "ID",
            hqCountry: data.account_type_detail.hq_country || "",
            code: data.account_type_detail.code || "",
            partnerExternalCode:
              data.account_type_detail.partner_external_code || "",
            parentId: data.account_type_detail.client_parent_id,
            firstLogin: data.account_type_detail.first_login,
            isCodDelivery: data.account_type_detail.partner_is_cod_delivery,
            partnerLocation: data.account_type_detail.partner_location,
            hub: data.account_type_detail.hub
              ? new Hub(
                  data.account_type_detail.hub?.hub_id,
                  data.account_type_detail.hub?.hub_name,
                  data.account_type_detail.hub?.hub_code,
                  data.account_type_detail.hub?.partner_id
                )
              : new Hub(0, "", "", 0),
            partnerIdSttReturn: data.account_type_detail.partner_id_stt_return,
            posParentId: data.account_type_detail.partner_pos_parent_id,
            partnerIsCodBooking:
              data.account_type_detail.partner_is_cod_booking,
            partnerIsCodDelivery:
              data.account_type_detail.partner_is_cod_delivery,
            partnerIsPcu: data.account_type_detail.partner_is_pcu,
            partnerPosSaldoDeposit:
              data.account_type_detail.partner_pos_saldo_deposit,
            partnerPosTieringLevel: data.account_type_detail.partner_pos_tiering_level?.toLowerCase(),
            isCod: data.account_type_detail.client_is_cod,
            isDfod: data.account_type_detail.client_is_dfod,
            isShowShippingRate:
              data.account_type_detail.client_show_shipping_tariff ??
              data.client_show_shipping_tariff ??
              false,
            partnerIsAllowBookVippack:
              data.account_type_detail.partner_is_allow_book_vippack,
            codConfigAmount: data.account_type_detail.client_cod_config_amount,
            divisionId: data.division_id,
            divisionName: data.division_name,
            status: data.account_type_detail.status?.toLowerCase()
          })
        : new AccountTypeDetail(),
      email: data.email,
      phone_number: data.phone_number,
      limit_rule_min_balance: data.limit_rule_min_balance,
      limit_rule_limit_border: data.limit_rule_limit_border,
      account_location: data.account_location,
      account_location_list: data.account_location_list,
      featureFlag: featureFlag
        ? featureFlag.map(
            (key) =>
              new FeatureFlagDetail({
                keyName: key.key_name,
                keyValue: key.key_value
              })
          )
        : [],
      assigned3lc: data.meta?.assigned_3lc,
      hqCountryName: data.hq_country_name || "-",
      hubDetail: this.mapHubDetail(data),
      shouldChangePasswordAt: data.should_change_password_at,
      shuttleId: data.shuttle_id,
      shuttleName: data.shuttle_name,
      isMaster: data.is_master,
      country_code: data.country_code,
      isLogin2FaEnable: data.is_login_2fa_enabled,
      isLogin2FaEnrolled: data.is_login_2fa_enrolled,
      isEligibleSetupOtp: data.is_eligible_setup_otp,
      countryCurrency: data.country_currency || "IDR"
    });
  }

  public mapHubDetail(data: any) {
    if (!data?.hub_detail?.hub_id) return undefined;
    return new HubDetail({
      hubId: data.hub_detail.hub_id,
      hubName: data.hub_detail.hub_name,
      hubCode: data.hub_detail.hub_code,
      originCity: data.hub_detail.origin_city,
      status: data.hub_detail.status,
      hubDistrictCode: data.hub_detail.hub_district_code
    });
  }

  public convertAccountDataFromApi(
    result: AxiosResponse<any>
  ): AccountEntities {
    const {
      data: { data }
    } = result;
    return IS_SENDER_ACCOUNT
      ? this.mapperForSenderAcount(data)
      : this.mapperForNonSenderAccount(data);
  }

  public convertDashboardDeliveryPosDataFromApi(
    result: AxiosResponse<any>
  ): DashboardDeliveryPos {
    const {
      data: { data }
    } = result;
    const pos = data.dashboard_insight_pos;
    const console = data.dashboard_insight_console;
    const subConsole = data.dashboard_insight_sub_console;

    return new DashboardDeliveryPos(
      pos
        ? pos.total_stt_del
        : console
        ? console.total_stt_del
        : subConsole && subConsole.total_stt_del,
      pos && pos.total_stt_dex,
      console && console.total_stt_sti,
      console && console.total_stt_sti_dest,
      console && console.total_stt_cargo_plane,
      console && console.total_stt_cargo_train,
      console && console.total_stt_cargo_truck,
      subConsole && subConsole.total_stt_sti_sc,
      subConsole && subConsole.total_stt_sti_dest_sc
    );
  }

  public convertChangeDataFromApi(result: AxiosResponse<any>): ResponsePayload {
    const { data } = result;
    return new ResponsePayload({
      success: data.success,
      message: data.message
    });
  }

  public convertValidationAccountDataFromApi(
    result: AxiosResponse<any>
  ): ValidationAccountData {
    const { data } = result;
    return new ValidationAccountData({ isAllow: data.data.is_allow });
  }
  public convertResponsePairTokenFromApi(
    result: AxiosResponse<any>
  ): ResponsePayload {
    const { data } = result;
    return new ResponsePayload({
      success: data.success,
      message: data.message
    });
  }

  public convertResponseUnpairTokenFromApi(
    result: AxiosResponse<any>
  ): ResponsePayload {
    const { data } = result;
    return new ResponsePayload({
      success: data.success,
      message: data.message
    });
  }

  public convertAccountStatusFromApi(
    result: AxiosResponse<any>
  ): ResponsePayloadV2 {
    const {
      data: { data }
    } = result;

    return new ResponsePayloadV2({
      data: new AccountStatus({
        status: !!data.cal_status.match(/^active$/)
      })
    });
  }

  public convertFormDataFromApi(result: AxiosResponse<any>): ResponsePayloadV2 {
    const {
      data: { data }
    } = result;

    return new ResponsePayloadV2({ data });
  }

  public convertListDeviceAuthFromApi(
    result: AxiosResponse<any>
  ): DeviceAuth[] {
    const {
      data: { data }
    } = result;

    return data?.length
      ? data.map(
          (item: any) =>
            new DeviceAuth({
              id: item.id,
              username: item.username,
              deviceId: item.device_id,
              deviceName: item.device_name,
              deviceType: item.device_type,
              deviceOs: item.device_os,
              lastLocationLogin: item.last_location_login,
              deviceMaster: item.device_master,
              latLong: item.lat_long,
              lastActiveAt: item.last_active_at,
              createdAt: item.created_at,
              approvedBy: item.approved_by,
              approvedAt: item.approved_at
            })
        )
      : [];
  }
}
